import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  MessageSquare, Plus, Archive, Search, Loader2,
  Wrench, LifeBuoy, ClipboardList, Users, Clock, Link2, X,
} from "lucide-react";
import api from "../lib/api";
import NewChatModal from "../components/chat/NewChatModal";
import { toast } from "../components/ui/sonner";

const KIND_META = {
  jobcard: { Icon: Wrench, color: "#F59E0B", label: "Job card" },
  ticket:  { Icon: LifeBuoy, color: "#F43F5E", label: "Ticket" },
  task:    { Icon: ClipboardList, color: "#34D399", label: "Task" },
  manual:  { Icon: Users, color: "#A78BFA", label: "Group" },
};

function fmtRelative(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 604800) return `${Math.floor(s / 86400)}d`;
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }); }
  catch { return ""; }
}

export default function ChatList() {
  const [threads, setThreads] = useState([]);
  const [archived, setArchived] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  // [Iter 343 — 2026-02-25] View mode is now a 3-way tab (active /
  // unread / archived) so techs with 50+ chats can jump straight to
  // the unread ones instead of scrolling to the bottom.
  // Dirk 2026-02-25: "we've got 54 chats and the unread ones are way
  // down at the bottom".
  const [viewMode, setViewMode] = useState("active");  // "active" | "unread" | "archived"
  const [showNew, setShowNew] = useState(false);
  const [customers, setCustomers] = useState({});
  // Wave 26.10 — long-press / right-click action sheet for a single thread.
  const [menuFor, setMenuFor] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const [a, b, c] = await Promise.all([
        api.get("/chats?archived=false"),
        api.get("/chats?archived=true").catch(() => ({ data: [] })),
        api.get("/customers").catch(() => ({ data: [] })),
      ]);
      setThreads(a.data || []);
      setArchived(b.data || []);
      const idx = {};
      (c.data || []).forEach((cust) => { idx[cust.id] = cust; });
      setCustomers(idx);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load chats.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, 5_000);
    return () => clearInterval(id);
  }, [load]);

  const archiveThread = useCallback(async (t) => {
    try {
      await api.post(`/chats/${t.id}/archive`);
      toast.success("Chat archived.");
      setMenuFor(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not archive chat.");
    }
  }, [load]);

  const unarchiveThread = useCallback(async (t) => {
    try {
      await api.post(`/chats/${t.id}/unarchive`);
      toast.success("Chat un-archived.");
      setMenuFor(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not un-archive chat.");
    }
  }, [load]);

  const goLinkedEntity = useCallback((t) => {
    setMenuFor(null);
    if (!t.kind || !t.entity_id) {
      toast.error("This chat isn't linked to an entity yet.");
      return;
    }
    const path = (
      t.kind === "jobcard" ? `/jobcards/${t.entity_id}`
      : t.kind === "ticket" ? `/tickets/${t.entity_id}`
      : t.kind === "task" ? `/tasks/${t.entity_id}`
      : null
    );
    if (path) navigate(path);
    else toast.info("This chat type doesn't have a linked entity to open.");
  }, [navigate]);

  // [Iter 343 — 2026-02-25] Compute the visible list based on viewMode.
  // Active tab: threads sorted with UNREAD ONES AT THE TOP so the
  // most-important conversations are the first thing the tech sees.
  // Unread tab: only threads with unread_count > 0.
  // Archived tab: unchanged.
  const _hasUnread = (t) => Number(t.unread_count || 0) > 0;
  const _sortActive = (a, b) => {
    const au = _hasUnread(a) ? 1 : 0;
    const bu = _hasUnread(b) ? 1 : 0;
    if (au !== bu) return bu - au;  // unread first
    // Within each group, newest message first (preserves the previous
    // behaviour for read chats).
    const at = a.last_message_at || a.created_at || "";
    const bt = b.last_message_at || b.created_at || "";
    return bt.localeCompare(at);
  };

  const unreadCount = threads.filter(_hasUnread).length;

  const sourceList = (() => {
    if (viewMode === "archived") return archived;
    if (viewMode === "unread") return threads.filter(_hasUnread);
    // active: unread pinned to the top
    return [...threads].sort(_sortActive);
  })();

  const filtered = sourceList.filter((t) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (t.title || "").toLowerCase().includes(q) ||
      (t.last_message_preview || "").toLowerCase().includes(q) ||
      (customers[t.customer_id]?.name || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-4" data-testid="chat-list-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight flex items-center gap-2">
            <MessageSquare className="w-7 h-7 text-rose-300" />
            Chat
          </h1>
          <p className="text-xs text-[#a0aabf] mt-1">
            Per-job and per-ticket conversations. Photos, voice notes, and files all live in the right place forever.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#647087]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search chats…"
              className="hv-input pl-8 text-xs w-56"
              data-testid="chat-list-search"
            />
          </div>
          <button onClick={() => setShowNew(true)} className="hv-btn hv-btn-primary text-xs" data-testid="chat-list-new-btn">
            <Plus className="w-3 h-3" /> New chat
          </button>
        </div>
      </div>

      {/* [Iter 343 — 2026-02-25] 3-way tab: Active / Unread (N) / Archived (N).
          The Unread tab is Dirk's request — with 50+ chats the unread
          ones would end up scrolled off screen. It also anchors the
          "unread pinned to top of Active" behaviour. */}
      <div className="flex items-center gap-1 border-b border-white/10" data-testid="chat-list-tabs">
        <button
          type="button"
          onClick={() => setViewMode("active")}
          className={`px-4 py-2 text-xs font-medium transition-colors border-b-2 ${
            viewMode === "active"
              ? "text-white border-[#1FB6C1]"
              : "text-[#647087] border-transparent hover:text-[#a0aabf]"
          }`}
          data-testid="chat-list-tab-active"
        >
          <MessageSquare className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
          Active ({threads.length})
        </button>
        <button
          type="button"
          onClick={() => setViewMode("unread")}
          disabled={unreadCount === 0}
          className={`px-4 py-2 text-xs font-medium transition-colors border-b-2 relative disabled:opacity-40 disabled:cursor-not-allowed ${
            viewMode === "unread"
              ? "text-white border-rose-400"
              : "text-[#647087] border-transparent hover:text-[#a0aabf]"
          }`}
          data-testid="chat-list-tab-unread"
        >
          <span className="inline-flex items-center gap-1.5">
            <span className={`inline-block w-2 h-2 rounded-full ${unreadCount > 0 ? "bg-rose-400 animate-pulse" : "bg-[#647087]"}`} />
            Unread ({unreadCount})
          </span>
        </button>
        <button
          type="button"
          onClick={() => setViewMode("archived")}
          className={`px-4 py-2 text-xs font-medium transition-colors border-b-2 ${
            viewMode === "archived"
              ? "text-white border-[#A78BFA]"
              : "text-[#647087] border-transparent hover:text-[#a0aabf]"
          }`}
          data-testid="chat-list-tab-archived"
        >
          <Archive className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
          Archived ({archived.length})
        </button>
      </div>

      {loading ? (
        <div className="hv-card p-6 text-sm text-[#a0aabf] flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading chats…
        </div>
      ) : filtered.length === 0 ? (
        <div className="hv-card p-10 text-center" data-testid="chat-list-empty">
          <MessageSquare className="w-10 h-10 text-[#647087] mx-auto mb-2" />
          <h3 className="font-display text-xl font-bold mb-1">
            {viewMode === "archived" ? "No archived chats"
              : viewMode === "unread" ? "All caught up"
              : "No active chats yet"}
          </h3>
          <p className="text-xs text-[#a0aabf]">
            {viewMode === "archived"
              ? "Closed jobs / tickets / tasks will appear here once their chats wrap up."
              : viewMode === "unread"
              ? "Every chat with new activity has been read. Nice work."
              : "Create a job card, ticket, or task — a chat will spin up automatically with the right people."}
          </p>
        </div>
      ) : (
        <div className="hv-card p-0 overflow-hidden divide-y divide-white/5">
          {filtered.map((t) => {
            const meta = KIND_META[t.kind] || KIND_META.manual;
            const Icon = meta.Icon;
            const cust = customers[t.customer_id];
            const memberCount = (t.member_ids || []).length;
            const unread = t.unread_count || 0;
            return (
              <Link
                to={`/chat/${t.id}`}
                key={t.id}
                className="flex items-start gap-3 p-3 hover:bg-white/[0.03] transition group"
                data-testid={`chat-thread-${t.id}`}
              >
                <div
                  className="w-10 h-10 rounded-md border flex items-center justify-center shrink-0 mt-0.5"
                  style={{ borderColor: `${meta.color}55`, background: `${meta.color}1a` }}
                >
                  <Icon className="w-5 h-5" style={{ color: meta.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className="text-[9px] uppercase tracking-[0.18em] font-bold px-1.5 py-0.5 rounded"
                      style={{ color: meta.color, background: `${meta.color}15`, border: `1px solid ${meta.color}33` }}
                    >
                      {meta.label}
                    </span>
                    <span className={`text-sm truncate ${unread > 0 ? "font-bold text-white" : "font-medium text-white/90"}`}>
                      {t.title}
                    </span>
                    {unread > 0 && (
                      <span
                        className="ml-auto bg-rose-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center"
                        data-testid={`chat-unread-${t.id}`}
                      >
                        {unread}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-[10px] text-[#a0aabf] mt-0.5">
                    {cust && (
                      <>
                        <span className="text-rose-200/80 font-medium truncate max-w-[200px]">{cust.name}</span>
                        <span>·</span>
                      </>
                    )}
                    <span className="inline-flex items-center gap-1">
                      <Users className="w-3 h-3" /> {memberCount}
                    </span>
                    <span>·</span>
                    <span className="inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {fmtRelative(t.last_message_at)}
                    </span>
                  </div>
                  {t.last_message_preview && (
                    <div className={`text-xs mt-1 line-clamp-1 ${unread > 0 ? "text-[#dde3ec]" : "text-[#9aa3b5]"}`}>
                      {t.last_message_preview}
                    </div>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {showNew && (
        <NewChatModal
          onClose={() => setShowNew(false)}
          onCreated={(t) => {
            setShowNew(false);
            navigate(`/chat/${t.id}`);
          }}
        />
      )}
    </div>
  );
}
